/**
 * LeRobot データセット（v2.x / v3.x）を VFS から読み、ビューア用の正規形にする。
 * 軌道データはエピソード単位の遅延読み込み（初期表示を軽くするため）。
 */
import { parquetReadObjects, parquetMetadataAsync, parquetSchema } from 'hyparquet';

/**
 * hyparquet-compressors は import しただけで snappy の WASM をコンパイルする。
 * WASM が禁止された環境（厳しい CSP、企業ポリシー）ではこれが例外になり、
 * 静的 import だとモジュールグラフごと落ちて UI が一切起動しなくなる。
 * そのため最初に parquet を読むときまで遅延し、失敗しても致命傷にしない。
 * コーデックが無い場合、SNAPPY は hyparquet 内蔵の JS 実装にフォールバックする
 * （LeRobot の parquet は SNAPPY なので、実質そのまま読める）。
 */
let compressorsPromise = null;
export function getCompressors(){
  return compressorsPromise ??= import('hyparquet-compressors')
    .then(m => m.compressors)
    .catch(err => {
      console.warn('圧縮コーデックを読み込めませんでした。内蔵の JS 実装で続行します', err);
      return {};
    });
}

/** "data/chunk-{chunk_index:03d}/file-{file_index:03d}.parquet" を埋める */
export function fmt(template, vars){
  return template.replace(/\{(\w+)(?::0(\d+)d)?\}/g, (_, key, width) => {
    const v = vars[key];
    if(v === undefined) throw new Error(`テンプレート変数が不足: ${key}`);
    return width ? String(v).padStart(+width, '0') : String(v);
  });
}

async function readParquet(entry, columns){
  const file = await entry.asyncBuffer();
  const compressors = await getCompressors();
  const metadata = await parquetMetadataAsync(file);
  const all = parquetSchema(metadata).children.map(c => c.element.name);
  const cols = columns ? columns.filter(c => all.includes(c)) : all;
  return { rows: await parquetReadObjects({ file, metadata, compressors, columns: cols }), all, metadata };
}

async function readParquetRange(entry, columns, rowStart, rowEnd){
  const file = await entry.asyncBuffer();
  const compressors = await getCompressors();
  const metadata = await parquetMetadataAsync(file);
  return parquetReadObjects({ file, metadata, compressors, columns, rowStart, rowEnd });
}

const num = v => typeof v === 'bigint' ? Number(v) : v;

/** エピソードのタスク欄は文字列 / 配列 / 欠損のいずれもありうる */
const taskArray = v =>
  (Array.isArray(v) ? [...v] : v == null ? [] : [v])
    .map(t => typeof t === 'string' ? t.trim() : String(t ?? '').trim())
    .filter(Boolean);
const arr = v => v == null ? null : Array.from(v, num);

async function jsonl(entry){
  const text = await entry.text();
  return text.split('\n').filter(l => l.trim()).map(l => JSON.parse(l));
}

export class Dataset {
  constructor(o){ Object.assign(this, o); this._traj = new Map(); }

  /** エピソードの軌道（state/action）を必要になった時だけ読む */
  async trajectory(i){
    if(this._traj.has(i)) return this._traj.get(i);
    const p = (async () => {
      const ep = this.episodes[i];
      const entry = this.vfs.get(ep.dataPath);
      if(!entry) throw new Error(`データファイルが見つかりません: ${ep.dataPath}`);
      const rows = await readParquetRange(entry, ['observation.state', 'action'], ep.rowStart, ep.rowEnd);
      const n = rows.length, J = this.joints.length;
      const state = Array.from({ length: J }, () => new Float32Array(n));
      const action = Array.from({ length: J }, () => new Float32Array(n));
      rows.forEach((r, k) => {
        const s = r['observation.state'], a = r.action;
        for(let j = 0; j < J; j++){ state[j][k] = num(s?.[j] ?? 0); action[j][k] = num(a?.[j] ?? 0); }
      });
      if(!this.range) this.range = this.joints.map((_, j) => {
        let lo = Infinity, hi = -Infinity;
        for(const v of state[j]){ if(v < lo) lo = v; if(v > hi) hi = v; }
        return [lo, hi];
      });
      return { state, action, n };
    })();
    this._traj.set(i, p);
    return p;
  }

  /** カメラ映像の Blob URL（同一ファイルは共有） */
  async videoUrl(i, cam){
    const path = this.episodes[i].cams[cam]?.path;
    if(!path) return null;
    const entry = this.vfs.get(path);
    if(!entry) return null;
    return entry.url();
  }

  get totalSec(){ return this.episodes.reduce((a, e) => a + e.sec, 0); }

  /** taskList 内の位置（色付けや並び替えのキー）。未知のタスクは -1 */
  taskRank(task){ return this.taskIndex.get(task) ?? -1; }
}

export async function loadDataset(vfs, onProgress = () => {}){
  const infoEntry = vfs.get('meta/info.json');
  if(!infoEntry) throw new Error('meta/info.json が見つかりません。LeRobot 形式のデータセットをドロップしてください。');
  onProgress({ phase: 'parse', detail: 'meta/info.json' });
  const info = await infoEntry.json();
  const version = String(info.codebase_version || 'v2.0');
  const v3 = /^v?3/.test(version.replace(/^v/, 'v'));

  const feats = info.features || {};
  const joints = feats.action?.names || feats['observation.state']?.names ||
                 Array.from({ length: feats.action?.shape?.[0] || 0 }, (_, i) => `j${i}`);
  const cams = Object.keys(feats).filter(k => feats[k]?.dtype === 'video' || feats[k]?.dtype === 'image');

  const ds = v3 ? await parseV3(vfs, info, joints, cams, onProgress)
                : await parseV2(vfs, info, joints, cams, onProgress);
  const catalog = await readTasks(vfs, v3, onProgress);
  const { taskList, taskIndex } = buildTaskList(catalog, ds.episodes);
  const { keys: channels, of: jointChannel } = channelsOf(joints);

  return new Dataset({
    vfs, info, version, joints, cams, taskList, taskIndex, channels, jointChannel,
    name: info.dataset_name || 'LeRobot dataset',
    robot: info.robot_type || 'unknown',
    fps: info.fps || 30,
    ...ds,
  });
}

/* ---------------- 信号系列 ---------------- */

/**
 * 関節名の末尾（`joint_1.pos` の pos）を信号種別として取り出す。
 * OpenArm の一部データセットは 1 関節につき pos / vel / torque の 3 本を持つので、
 * 全部を同じチャートに重ねると読めない。種別が 2 つ以上あるときだけ「分かれている」とみなし、
 * UI 側は既定で先頭の種別（= pos）だけを描く。
 * 種別が 1 つ（so101 や OpenArm の 8 関節版）なら従来どおり全部描く。
 */
export function channelsOf(joints){
  const of = joints.map(n => /\.([^.]+)$/.exec(n)?.[1] ?? '');
  const keys = [...new Set(of)];
  return of.every(Boolean) && keys.length > 1 ? { keys, of } : { keys: [], of };
}

/* ---------------- タスク ---------------- */

/**
 * meta/tasks.{parquet,jsonl} のタスク一覧。エピソード側にも task 文字列は入っているが、
 * ここを読むと「エピソードが 0 件のタスク」も含めた本来の種類数が分かる。
 * 無くても致命的ではないので、読めなければ空で続行する。
 */
async function readTasks(vfs, v3, onProgress = () => {}){
  const path = v3 ? 'meta/tasks.parquet' : 'meta/tasks.jsonl';
  const entry = vfs.get(path);
  if(!entry) return [];
  onProgress({ phase: 'parse', detail: path });
  try {
    const rows = v3 ? (await readParquet(entry, ['task_index', 'task'])).rows : await jsonl(entry);
    return rows
      .filter(r => typeof r.task === 'string' && r.task !== '')
      .map(r => ({ task: r.task, index: num(r.task_index) ?? 0 }))
      .sort((a, b) => a.index - b.index);
  } catch (err){
    console.warn(`${path} を読めませんでした。エピソード側のタスク名だけで続行します`, err);
    return [];
  }
}

/** カタログ順 → エピソードにしか出てこないタスクの順で並べ、件数を数える */
function buildTaskList(catalog, episodes){
  const counts = new Map();
  for(const e of episodes) for(const t of e.tasks) counts.set(t, (counts.get(t) || 0) + 1);

  const taskList = [];
  const seen = new Set();
  for(const { task } of catalog){
    if(seen.has(task)) continue;
    seen.add(task);
    taskList.push({ task, n: counts.get(task) || 0 });
  }
  for(const task of counts.keys()){
    if(seen.has(task)) continue;
    seen.add(task);
    taskList.push({ task, n: counts.get(task) });
  }
  return { taskList, taskIndex: new Map(taskList.map((t, k) => [t.task, k])) };
}

/* ---------------- v3.x ---------------- */

async function parseV3(vfs, info, joints, cams, onProgress){
  const metaFiles = vfs.glob(/^meta\/episodes\/.*\.parquet$/);
  if(!metaFiles.length) throw new Error('meta/episodes/*.parquet が見つかりません（v3.0 形式）');

  const want = ['episode_index', 'tasks', 'length', 'data/chunk_index', 'data/file_index',
                'dataset_from_index', 'dataset_to_index'];
  for(const c of cams) want.push(...[`videos/${c}/chunk_index`, `videos/${c}/file_index`,
                                     `videos/${c}/from_timestamp`, `videos/${c}/to_timestamp`]);
  for(const k of ['action', 'observation.state']) want.push(`stats/${k}/min`, `stats/${k}/max`);

  let rows = [];
  for(const p of metaFiles){
    onProgress({ phase: 'parse', detail: p });
    const { rows: r } = await readParquet(vfs.get(p), want);
    rows.push(...r);
  }
  rows.sort((a, b) => num(a.episode_index) - num(b.episode_index));

  const dataTpl = info.data_path || 'data/chunk-{chunk_index:03d}/file-{file_index:03d}.parquet';
  const vidTpl = info.video_path || 'videos/{video_key}/chunk-{chunk_index:03d}/file-{file_index:03d}.mp4';
  const fps = info.fps || 30;

  // data ファイルごとの先頭行インデックス（グローバル index → ファイル内 row）
  const base = new Map();
  for(const r of rows){
    const key = `${num(r['data/chunk_index'])}/${num(r['data/file_index'])}`;
    const from = num(r.dataset_from_index);
    if(!base.has(key) || from < base.get(key)) base.set(key, from);
  }

  const episodes = rows.map(r => {
    const i = num(r.episode_index);
    const len = num(r.length);
    const key = `${num(r['data/chunk_index'])}/${num(r['data/file_index'])}`;
    const off = base.get(key);
    const camMap = {};
    for(const c of cams){
      const ci = r[`videos/${c}/chunk_index`], fi = r[`videos/${c}/file_index`];
      if(ci == null || fi == null) continue;
      camMap[c] = {
        path: fmt(vidTpl, { video_key: c, chunk_index: num(ci), file_index: num(fi) }),
        a: Number(r[`videos/${c}/from_timestamp`] ?? 0),
        b: Number(r[`videos/${c}/to_timestamp`] ?? len / fps),
      };
    }
    const tasks = taskArray(r.tasks);
    return {
      i, len, sec: len / fps,
      tasks, task: tasks[0] ?? '',
      dataPath: fmt(dataTpl, { chunk_index: num(r['data/chunk_index']), file_index: num(r['data/file_index']) }),
      rowStart: num(r.dataset_from_index) - off,
      rowEnd: num(r.dataset_to_index) - off,
      cams: camMap,
    };
  });

  return { episodes, range: rangeFromStats(rows, joints.length) };
}

function rangeFromStats(rows, J){
  const lo = new Array(J).fill(Infinity), hi = new Array(J).fill(-Infinity);
  let seen = false;
  for(const r of rows){
    const mn = arr(r['stats/action/min']), mx = arr(r['stats/action/max']);
    if(!mn || !mx) continue;
    seen = true;
    for(let j = 0; j < J; j++){ lo[j] = Math.min(lo[j], mn[j]); hi[j] = Math.max(hi[j], mx[j]); }
  }
  return seen ? lo.map((v, j) => [v, hi[j]]) : null;
}

/* ---------------- v2.x ---------------- */

async function parseV2(vfs, info, joints, cams, onProgress){
  const epEntry = vfs.get('meta/episodes.jsonl');
  if(!epEntry) throw new Error('meta/episodes.jsonl が見つかりません（v2.x 形式）');
  onProgress({ phase: 'parse', detail: 'meta/episodes.jsonl' });
  const meta = await jsonl(epEntry);

  const fps = info.fps || 30;
  const chunks = info.chunks_size || 1000;
  const dataTpl = info.data_path || 'data/chunk-{episode_chunk:03d}/episode_{episode_index:06d}.parquet';
  const vidTpl = info.video_path || 'videos/chunk-{episode_chunk:03d}/{video_key}/episode_{episode_index:06d}.mp4';

  const episodes = meta.map(m => {
    const i = m.episode_index, len = m.length;
    const vars = { episode_chunk: Math.floor(i / chunks), episode_index: i };
    const camMap = {};
    for(const c of cams) camMap[c] = { path: fmt(vidTpl, { ...vars, video_key: c }), a: 0, b: len / fps };
    const tasks = taskArray(m.tasks ?? m.task);
    return {
      i, len, sec: len / fps,
      tasks, task: tasks[0] ?? '',
      dataPath: fmt(dataTpl, vars),
      rowStart: 0, rowEnd: len,
      cams: camMap,
    };
  }).sort((a, b) => a.i - b.i);

  return { episodes, range: await rangeV2(vfs, joints.length) };
}

async function rangeV2(vfs, J){
  const st = vfs.get('meta/stats.json');
  if(st){
    try {
      const s = await st.json();
      const a = s.action || s['action'];
      if(a?.min && a?.max) return a.min.map((v, j) => [v, a.max[j]]);
    } catch { /* 無視してフォールバック */ }
  }
  const es = vfs.get('meta/episodes_stats.jsonl');
  if(es){
    try {
      const lo = new Array(J).fill(Infinity), hi = new Array(J).fill(-Infinity);
      for(const r of await jsonl(es)){
        const a = r.stats?.action;
        if(!a) continue;
        for(let j = 0; j < J; j++){ lo[j] = Math.min(lo[j], a.min[j]); hi[j] = Math.max(hi[j], a.max[j]); }
      }
      if(lo.every(Number.isFinite)) return lo.map((v, j) => [v, hi[j]]);
    } catch { /* 無視 */ }
  }
  return null;                      // 最初の軌道読み込み時に算出する
}
